import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { humanizeEnum } from '../../utils/format';
import { TaskService } from '../../services/task.service';
import { ITask, TaskStatus } from '../../interfaces/task.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import TaskListTable from './TaskListTable';
import TaskForm from './TaskForm';

const PAGE_SIZE = 10;

const TaskAdminList: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ITask | null>(null);
    const [status, setStatus] = useState<TaskStatus | ''>(''); // F8 (incl. Blocked)

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await TaskService.adminList(institutionId, p, PAGE_SIZE, status ? [status] : undefined);
            setTasks(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, status]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (t: ITask) => { setEditing(t); setFormOpen(true); };

    const handleDelete = async (t: ITask) => {
        if (!window.confirm(`Delete task "${t.title}"?`)) return;
        try {
            await TaskService.delete(t.id, institutionId);
            toast.showSuccess('Task deleted');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to delete task');
        }
    };

    const handleStatusChange = async (t: ITask, next: TaskStatus, blockedReason?: string) => {
        try {
            await TaskService.updateStatus(t.id, next, blockedReason);
            toast.showSuccess('Status updated');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to update status');
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">All Tasks</Typography>
                <Button data-help="tasks.admin.newTask" variant="contained" onClick={openCreate}>New Task</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField data-help="tasks.admin.field.status" select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 240 }}
                    onChange={e => { setStatus(e.target.value as TaskStatus | ''); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    {(['OPEN', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELLED'] as TaskStatus[]).map(s => (
                        <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <TaskListTable
                tasks={tasks}
                loading={loading}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                mode="admin"
                onStatusChange={handleStatusChange}
                onEdit={openEdit}
                onDelete={handleDelete}
            />
            <TaskForm
                open={formOpen}
                task={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => fetchList(page)}
            />
        </Box>
    );
};

export default TaskAdminList;
